<script setup lang="ts">
  import type {
    ChangeStakeholderBillingDetailsRequestPayload,
    Client
  } from '@gat-ship/types'
  import { testIds } from '@gat-ship/types'
  import {
    FormField,
    FormSubmit,
    InputGroupTextarea,
    RadioCardGroup,
    RadioCardGroupItem,
    Button,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderTitle,
    ModalSection,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { ref } from 'vue'
  import { IconCopy, IconEdit } from '@gat-ship/icons'
  import { FieldGroup } from '@gat-ship/vue-components'
  import { text } from '@gat-ship/stakeholders'
  import { useStakeholderContext } from '../../composables/useStakeholder'
  import type { ShowStakeholderUpdateBillingAddressParams } from '../providers/stakeholder-modals'

  // Edit-modal setup (open model + injected service + staged stakeholder +
  // title/description refs) is the same idiomatic boilerplate in every modal.
  // fallow-ignore-next-line code-duplication
  const isOpen = defineModel<boolean>('open', { default: false })
  const service = useStakeholderContext()

  const _stakeholder = ref<Client>()
  const modalTitle = ref('')
  const modalDescription = ref('')

  const schema = z
    .object({
      stakeholderId: z.string(),
      billingAddressType: z.enum(['same-as-postal', 'custom']),
      billingAddress: z.string().optional()
    })
    .superRefine((data, ctx) => {
      if (
        data.billingAddressType === 'custom' &&
        (!data.billingAddress || data.billingAddress.length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['billingAddress'],
          message: 'Billing address is required'
        })
      }
    })
  type FormValues = z.infer<typeof schema>

  /** Echo the stakeholder's current billing fields back unchanged. */
  function unchangedBillingFields(stakeholder: Client | undefined) {
    if (!stakeholder) {
      return {
        billingCurrencyCode: null,
        invoiceEmail: null,
        paymentTermsPeriodInDays: null,
        paymentTermsUseRunningMonth: null,
        useDefaultBillingCurrency: false,
        useDefaultPaymentTerms: false
      }
    }
    return {
      billingCurrencyCode: stakeholder.billingCurrency?.code ?? null,
      invoiceEmail: stakeholder.invoiceEmail ?? null,
      paymentTermsPeriodInDays: stakeholder.paymentTermsPeriodInDays ?? null,
      paymentTermsUseRunningMonth:
        stakeholder.paymentTermsUseRunningMonth ?? null,
      useDefaultBillingCurrency: stakeholder.useDefaultBillingCurrency ?? false,
      useDefaultPaymentTerms: stakeholder.useDefaultPaymentTerms ?? false
    }
  }

  const form = useForm({
    defaultValues: {
      stakeholderId: '',
      billingAddressType: 'same-as-postal',
      billingAddress: ''
    } as FormValues,
    validators: {
      onChange: schema
    },
    onSubmit: async ({ value }) => {
      const isSameAsPostal = value.billingAddressType === 'same-as-postal'

      // Send `null` (not `undefined`) to clear the billing address on the
      // backend — an omitted key leaves the field unchanged, so toggling to
      // "same as postal" with `undefined` would leave a stale custom address
      // in the DB, contradicting the flag.
      const payload: ChangeStakeholderBillingDetailsRequestPayload = {
        billingAddressSameAsPostal: isSameAsPostal,
        billingAddress: isSameAsPostal ? null : value.billingAddress || null,
        ...unchangedBillingFields(_stakeholder.value)
      }
      await service?.commands.changeBillingDetails(value.stakeholderId, payload)
      isOpen.value = false
    }
  })

  function showModal({
    stakeholder
  }: ShowStakeholderUpdateBillingAddressParams) {
    _stakeholder.value = stakeholder
    modalTitle.value = text.groups.billingAddress(
      stakeholder.stakeholderType
    ).modals.changeBillingAddress.title
    modalDescription.value = text.groups
      .billingAddress(stakeholder.stakeholderType)
      .modals.changeBillingAddress.description(stakeholder.name ?? '')
    form.reset({
      stakeholderId: stakeholder.id,
      billingAddressType: stakeholder.billingAddressSameAsPostal
        ? 'same-as-postal'
        : 'custom',
      billingAddress: stakeholder.billingAddress ?? ''
    })
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        {{ modalTitle }}
      </ModalHeaderTitle>
      <ModalHeaderDescription v-if="modalDescription">
        {{ modalDescription }}
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <FormField :form="form" name="billingAddressType" :input-group="false">
          <template #default="{ field: typeField }">
            <RadioCardGroup
              :model-value="typeField.state.value"
              @update:model-value="typeField.handleChange"
            >
              <RadioCardGroupItem
                value="same-as-postal"
                label="Same as Postal Address"
                description="The billing address is the same as the postal address"
                :icon="IconCopy"
                :data-testid="
                  testIds.stakeholders.forms.billingAddress.sameAsPostalRadio
                "
              />
              <RadioCardGroupItem
                value="custom"
                label="Custom Billing Address"
                description="Manually set the billing address"
                :icon="IconEdit"
                :data-testid="
                  testIds.stakeholders.forms.billingAddress
                    .customBillingAddressRadio
                "
              >
                <template #footer="{ selected }">
                  <FieldGroup v-if="selected">
                    <FormField
                      :form="form"
                      name="billingAddress"
                      label="Billing Address"
                      required
                    >
                      <template #default="{ field }">
                        <InputGroupTextarea
                          :id="field.name"
                          :model-value="field.state.value ?? ''"
                          :data-testid="
                            testIds.stakeholders.forms.billingAddress
                              .addressInput
                          "
                          @update:model-value="field.handleChange"
                          @blur="field.handleBlur"
                        />
                      </template>
                    </FormField>
                  </FieldGroup>
                </template>
              </RadioCardGroupItem>
            </RadioCardGroup>
          </template>
        </FormField>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit :form="form" label="Save Changes" />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
